import { useState } from 'react';
import { Home, DollarSign, BarChart3, ShieldCheck, ChevronDown, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import type {
  UnitCountReconciliation,
  RentDecision,
  OpExBreakdownResponse,
} from '../../types/calculations';

interface DataFoundationBarProps {
  units: number;
  unitSource?: string;
  unitConfidence?: string;
  unitCountReconciliation?: UnitCountReconciliation;
  rentPerUnit: number;
  rentSource?: string;
  rentConfidence?: string;
  rentDecision?: RentDecision;
  opexRate: number;
  opexTier?: string;
  opexBreakdown?: OpExBreakdownResponse;
  dataConfidence?: 'high' | 'medium' | 'low';
  sourcesChecked?: string[];
}

type ExpandedPanel = 'units' | 'rent' | 'opex' | 'quality' | null;

function confidenceDot(confidence?: string) {
  const c = (confidence || '').toUpperCase();
  if (c === 'HIGH') return 'bg-emerald-400';
  if (c === 'MEDIUM') return 'bg-amber-400';
  if (c === 'LOW' || c === 'FALLBACK') return 'bg-red-400';
  return 'bg-stone-500';
}

function confidenceLabel(confidence?: string) {
  const c = (confidence || '').toUpperCase();
  if (c === 'HIGH') return 'HIGH';
  if (c === 'MEDIUM') return 'MED';
  if (c === 'LOW' || c === 'FALLBACK') return 'LOW';
  return '—';
}

function overallConfidenceLabel(dataConfidence?: string) {
  const c = (dataConfidence || '').toLowerCase();
  if (c === 'high') return 'HIGH';
  if (c === 'medium') return 'MED';
  if (c === 'low') return 'LOW';
  return '—';
}

interface CellProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  detail?: string;
  isExpanded: boolean;
  onClick: () => void;
}

function Cell({ icon, title, value, subtitle, detail, isExpanded, onClick }: CellProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-0 flex flex-col items-center px-3 py-2.5 cursor-pointer group transition-colors hover:bg-charcoal/80 rounded-md"
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-stone-700 group-hover:text-stone-700 transition-colors">{icon}</span>
        <span className="text-sm uppercase tracking-wider text-stone-700 whitespace-nowrap">{title}</span>
      </div>
      <span className="text-base font-mono tabular-nums font-bold text-ivory whitespace-nowrap">{value}</span>
      <span className="text-sm text-stone-700 whitespace-nowrap">{subtitle}</span>
      {detail && (
        <span className="text-sm text-stone-700 whitespace-nowrap">{detail}</span>
      )}
      <ChevronDown
        size={10}
        className={`mt-0.5 text-stone-700 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

/* ── Expand Panel: Units ── */
function UnitsPanel({ reconciliation }: { reconciliation?: UnitCountReconciliation }) {
  if (!reconciliation) {
    return <p className="text-sm text-stone-700">No unit reconciliation data available.</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold tracking-widest text-gold uppercase">Unit Count Reconciliation</h4>
        {reconciliation.conflict_detected && (
          <div className="flex items-center gap-1 text-amber-400">
            <AlertTriangle size={12} />
            <span className="text-sm font-medium">Conflict Detected</span>
          </div>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-sm uppercase tracking-wider text-stone-700 border-b border-slate-dark">
            <th className="text-left py-1.5 font-medium">Source</th>
            <th className="text-center py-1.5 font-medium">Units</th>
            <th className="text-center py-1.5 font-medium">Confidence</th>
            <th className="text-left py-1.5 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {reconciliation.sources_checked.map((src) => (
            <tr key={src.source_name} className="border-b border-slate-dark/50">
              <td className="py-1.5 text-ivory text-sm">
                {src.source_name}
                {src.source_name === reconciliation.resolved_source && (
                  <span className="ml-1.5 text-sm bg-gold/20 text-gold px-1.5 py-0.5 rounded">SELECTED</span>
                )}
              </td>
              <td className="text-center py-1.5 font-mono tabular-nums text-ivory text-sm">{src.unit_count}</td>
              <td className="text-center py-1.5">
                <div className="flex items-center justify-center gap-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${confidenceDot(src.confidence)}`} />
                  <span className="text-sm text-stone-700 uppercase">{src.confidence}</span>
                </div>
              </td>
              <td className="py-1.5 text-sm text-stone-700">{src.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {reconciliation.conflict_details && (
        <div className="bg-amber-900/10 border border-amber-600/20 rounded px-3 py-2">
          <p className="text-sm text-amber-400">{reconciliation.conflict_details}</p>
        </div>
      )}
      <div className="text-sm text-stone-700">
        Resolved: <span className="text-ivory font-mono">{reconciliation.resolved_count}</span> units
        from <span className="text-ivory">{reconciliation.resolved_source}</span>
        {' '}(<span className="text-stone-700">{reconciliation.resolved_confidence} confidence</span>)
      </div>
    </div>
  );
}

/* ── Expand Panel: Rent ── */
function RentPanel({ rentDecision }: { rentDecision?: RentDecision }) {
  if (!rentDecision || !rentDecision.sources?.length) {
    return <p className="text-sm text-stone-700">No rent source data available.</p>;
  }
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold tracking-widest text-gold uppercase">Rent Source Analysis</h4>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-sm uppercase tracking-wider text-stone-700 border-b border-slate-dark">
            <th className="text-left py-1.5 font-medium">Source</th>
            <th className="text-right py-1.5 font-medium">Rent/Unit</th>
            <th className="text-center py-1.5 font-medium">Confidence</th>
            <th className="text-center py-1.5 font-medium">Used For</th>
          </tr>
        </thead>
        <tbody>
          {rentDecision.sources.map((src) => (
            <tr key={src.name} className="border-b border-slate-dark/50">
              <td className="py-1.5 text-ivory text-sm">{src.name}</td>
              <td className="text-right py-1.5 font-mono tabular-nums text-ivory text-sm">{formatCurrency(src.value)}</td>
              <td className="text-center py-1.5">
                <div className="flex items-center justify-center gap-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${confidenceDot(src.confidence)}`} />
                  <span className="text-sm text-stone-700 uppercase">{src.confidence}</span>
                </div>
              </td>
              <td className="text-center py-1.5">
                {src.selected_for ? (
                  <span className="text-sm bg-gold/20 text-gold px-1.5 py-0.5 rounded">{src.selected_for}</span>
                ) : (
                  <span className="text-sm text-stone-700">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rentDecision.decision_rationale && (
        <div className="bg-charcoal/60 border border-slate-dark rounded px-3 py-2">
          <p className="text-sm text-stone-700">{rentDecision.decision_rationale}</p>
        </div>
      )}
      {rentDecision.divergence_flags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rentDecision.divergence_flags.map((flag, i) => (
            <span key={i} className="text-sm bg-amber-900/20 text-amber-400 border border-amber-600/20 px-2 py-0.5 rounded">
              {flag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Expand Panel: OpEx ── */
function OpExPanel({ opexBreakdown }: { opexBreakdown?: OpExBreakdownResponse }) {
  if (!opexBreakdown || !opexBreakdown.line_items?.length) {
    return <p className="text-sm text-stone-700">No operating expense breakdown available.</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold tracking-widest text-gold uppercase">Operating Expense Breakdown</h4>
        <span className="text-sm text-stone-700">Tier: {opexBreakdown.tier}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-sm uppercase tracking-wider text-stone-700 border-b border-slate-dark">
            <th className="text-left py-1.5 font-medium">Category</th>
            <th className="text-right py-1.5 font-medium">Rate</th>
            <th className="text-right py-1.5 font-medium">Amount</th>
            <th className="text-left py-1.5 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {opexBreakdown.line_items.map((item) => (
            <tr key={item.category} className="border-b border-slate-dark/50">
              <td className="py-1.5 text-ivory text-sm">{item.category}</td>
              <td className="text-right py-1.5 font-mono tabular-nums text-stone-700 text-sm">
                {(item.rate * 100).toFixed(1)}%
              </td>
              <td className="text-right py-1.5 font-mono text-ivory text-sm">{formatCurrency(item.amount)}</td>
              <td className="py-1.5 text-sm text-stone-700">{item.source || 'SSOT'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gold/20">
            <td className="py-1.5 text-sm text-gold font-medium">Total</td>
            <td className="text-right py-1.5 font-mono tabular-nums text-gold text-sm">
              {(opexBreakdown.total_rate * 100).toFixed(1)}%
            </td>
            <td className="text-right py-1.5 font-mono tabular-nums text-gold text-sm">{formatCurrency(opexBreakdown.total_amount)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ── Expand Panel: Data Quality ── */
function DataQualityPanel({
  dataConfidence,
  unitConfidence,
  rentConfidence,
  sourcesChecked,
}: {
  dataConfidence?: string;
  unitConfidence?: string;
  rentConfidence?: string;
  sourcesChecked?: string[];
}) {
  const signals = [
    { label: 'Overall', confidence: dataConfidence },
    { label: 'Unit Count', confidence: unitConfidence },
    { label: 'Rent', confidence: rentConfidence },
  ];
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold tracking-widest text-gold uppercase">Data Quality Assessment</h4>
      <div className="grid grid-cols-3 gap-3">
        {signals.map((s) => (
          <div key={s.label} className="bg-charcoal/60 border border-slate-dark rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <div className={`w-2 h-2 rounded-full ${confidenceDot(s.confidence)}`} />
              <span className="text-sm uppercase tracking-wider text-stone-700">{s.label}</span>
            </div>
            <span className="text-sm font-bold text-ivory">{confidenceLabel(s.confidence)}</span>
          </div>
        ))}
      </div>
      {sourcesChecked && sourcesChecked.length > 0 && (
        <div>
          <p className="text-sm text-stone-700 mb-1.5">Sources Consulted:</p>
          <div className="flex flex-wrap gap-1.5">
            {sourcesChecked.map((src) => (
              <span key={src} className="text-sm bg-charcoal border border-slate-dark text-stone-700 px-2 py-0.5 rounded">
                {src}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Component ── */
export function DataFoundationBar({
  units,
  unitSource,
  unitConfidence,
  unitCountReconciliation,
  rentPerUnit,
  rentSource,
  rentConfidence,
  rentDecision,
  opexRate,
  opexTier,
  opexBreakdown,
  dataConfidence,
  sourcesChecked,
}: DataFoundationBarProps) {
  const [expanded, setExpanded] = useState<ExpandedPanel>(null);

  const toggle = (panel: ExpandedPanel) => {
    setExpanded((prev) => (prev === panel ? null : panel));
  };

  const opexPct = opexRate < 1 ? opexRate * 100 : opexRate;
  const lineItemCount = opexBreakdown?.line_items?.length ?? 0;
  const sourceCount = rentDecision?.sources?.length ?? (sourcesChecked?.length ?? 0);
  const totalPossible = 4; // RentCast, Rentometer, Zillow, L&I

  return (
    <div className="bg-charcoal/40 border border-gold/10 rounded-lg overflow-hidden">
      {/* Compact bar */}
      <div className="flex items-stretch divide-x divide-slate-dark">
        <Cell
          icon={<Home size={12} />}
          title="Units"
          value={String(units)}
          subtitle={unitSource || 'Unknown'}
          detail={`${confidenceLabel(unitConfidence)} conf`}
          isExpanded={expanded === 'units'}
          onClick={() => toggle('units')}
        />
        <Cell
          icon={<DollarSign size={12} />}
          title="Rent/Unit"
          value={formatCurrency(rentPerUnit)}
          subtitle={rentSource || 'Estimate'}
          detail={`${confidenceLabel(rentConfidence)} conf`}
          isExpanded={expanded === 'rent'}
          onClick={() => toggle('rent')}
        />
        <Cell
          icon={<BarChart3 size={12} />}
          title="OpEx Rate"
          value={`${opexPct.toFixed(1)}%`}
          subtitle={opexTier || 'Tier2'}
          detail={lineItemCount > 0 ? `${lineItemCount} line items` : undefined}
          isExpanded={expanded === 'opex'}
          onClick={() => toggle('opex')}
        />
        <Cell
          icon={<ShieldCheck size={12} />}
          title="Data Quality"
          value={overallConfidenceLabel(dataConfidence)}
          subtitle={`${sourceCount}/${totalPossible} sources`}
          detail="available"
          isExpanded={expanded === 'quality'}
          onClick={() => toggle('quality')}
        />
      </div>

      {/* Expandable detail panels */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-4 border-t border-slate-dark">
            {expanded === 'units' && <UnitsPanel reconciliation={unitCountReconciliation} />}
            {expanded === 'rent' && <RentPanel rentDecision={rentDecision} />}
            {expanded === 'opex' && <OpExPanel opexBreakdown={opexBreakdown} />}
            {expanded === 'quality' && (
              <DataQualityPanel
                dataConfidence={dataConfidence}
                unitConfidence={unitConfidence}
                rentConfidence={rentConfidence}
                sourcesChecked={sourcesChecked}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
